import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useSubscription } from '@apollo/client';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCorners,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { Search, Filter as FilterIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import {
  GET_PROJECT,
  GET_PROJECT_COLUMNS,
  GET_PROJECT_TASKS,
  MOVE_TASK_MUTATION,
  REORDER_COLUMNS_MUTATION,
  PROJECT_BOARD_CHANGED_SUBSCRIPTION,
} from '../../graphql/queries';
import { Input } from '../ui/input';
import { KanbanColumn, KanbanColumnData } from './KanbanColumn';
import { TaskCard, TaskCardData } from './TaskCard';
import { TaskDialog, TaskDialogTask } from './TaskDialog';
import {
  TASK_PRIORITY_LABELS,
  TASK_PRIORITY_OPTIONS,
  TaskPriority,
} from './projectUtils';
import { usePermission } from '../../hooks/usePermission';

interface ProjectKanbanBoardProps {
  projectId: string;
  currentUserId?: string;
  canManageProject?: boolean;
}

export const ProjectKanbanBoard: React.FC<ProjectKanbanBoardProps> = ({
  projectId,
  currentUserId,
  canManageProject,
}) => {
  const canConfigure = usePermission('kanban.configure');
  const canCreateTasks = usePermission('tasks.create');

  const {
    data: columnsData,
    loading: columnsLoading,
    refetch: refetchColumns,
  } = useQuery<{ projectColumns: KanbanColumnData[] }>(GET_PROJECT_COLUMNS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: tasksData,
    loading: tasksLoading,
    refetch: refetchTasks,
  } = useQuery<{ projectTasks: TaskCardData[] }>(GET_PROJECT_TASKS, {
    variables: { projectId },
    fetchPolicy: 'cache-and-network',
  });

  const [moveTask] = useMutation(MOVE_TASK_MUTATION);
  const [reorderColumns] = useMutation(REORDER_COLUMNS_MUTATION);

  // Subscribe to board events for real-time updates
  useSubscription(PROJECT_BOARD_CHANGED_SUBSCRIPTION, {
    variables: { projectId },
    onData: () => {
      // Simple v1: refetch on any change
      refetchTasks().catch(() => {});
      refetchColumns().catch(() => {});
    },
    onError: (error) => {
      // Don't toast — subscription errors are expected during reconnects
      console.debug('Project board subscription error:', error);
    },
  });

  // ==== Filters ====
  const [filterMine, setFilterMine] = useState(false);
  const [filterUnassigned, setFilterUnassigned] = useState(false);
  const [filterPriorities, setFilterPriorities] = useState<TaskPriority[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // ==== Local state for optimistic moves & dialogs ====
  const [activeDragTask, setActiveDragTask] = useState<TaskCardData | null>(
    null,
  );
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskDialogTask | null>(null);
  const [createColumnId, setCreateColumnId] = useState<string | null>(null);

  // Deep-link: ?task=<id> opens that task in the dialog (used by /mentions).
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkTaskId = searchParams.get('task');
  useEffect(() => {
    if (!deepLinkTaskId) return;
    const found = (tasksData?.projectTasks || []).find(
      (t) => t.id === deepLinkTaskId,
    );
    if (found) {
      setEditingTask({
        id: found.id,
        name: found.name,
        description: found.description,
        priority: found.priority,
        startDate: found.startDate,
        plannedEndDate: found.plannedEndDate,
        columnId: found.columnId,
        assignees: found.assignees || [],
      });
      setCreateColumnId(null);
      setTaskDialogOpen(true);
      // Clear the param so re-opening behavior is sane.
      const next = new URLSearchParams(searchParams);
      next.delete('task');
      setSearchParams(next, { replace: true });
    }
  }, [deepLinkTaskId, tasksData, searchParams, setSearchParams]);

  const columns = useMemo(() => {
    return [...(columnsData?.projectColumns || [])].sort(
      (a, b) => a.order - b.order,
    );
  }, [columnsData]);

  const allTasks = useMemo(
    () => tasksData?.projectTasks || [],
    [tasksData],
  );

  // Apply filters
  const filteredTasks = useMemo(() => {
    return allTasks.filter((t) => {
      if (filterMine && currentUserId) {
        const isMine = (t.assignees || []).some(
          (a) => a.employeeId === currentUserId,
        );
        if (!isMine) return false;
      }
      if (filterUnassigned) {
        if ((t.assignees || []).length > 0) return false;
      }
      if (filterPriorities.length > 0) {
        if (!filterPriorities.includes(t.priority)) return false;
      }
      if (debouncedSearch) {
        const q = debouncedSearch.toLowerCase();
        if (!t.name.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [
    allTasks,
    filterMine,
    filterUnassigned,
    filterPriorities,
    debouncedSearch,
    currentUserId,
  ]);

  // Group tasks by column, sorted by orderInColumn
  const tasksByColumn = useMemo(() => {
    const map: Record<string, TaskCardData[]> = {};
    columns.forEach((c) => {
      map[c.id] = [];
    });
    filteredTasks.forEach((t) => {
      if (!map[t.columnId]) map[t.columnId] = [];
      map[t.columnId].push(t);
    });
    Object.keys(map).forEach((k) => {
      map[k].sort((a, b) => a.orderInColumn - b.orderInColumn);
    });
    return map;
  }, [columns, filteredTasks]);

  // ==== DnD ====
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as any;
    if (data?.type === 'task') {
      setActiveDragTask(data.task);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveDragTask(null);
    if (!over) return;

    const activeData = active.data.current as any;
    const overData = over.data.current as any;

    // Column reorder
    if (
      activeData?.type === 'column-sort' &&
      overData?.type === 'column-sort'
    ) {
      if (!canConfigure) return;
      const fromId = activeData.columnId as string;
      const toId = overData.columnId as string;
      if (fromId === toId) return;
      const ids = columns.map((c) => c.id);
      const fromIdx = ids.indexOf(fromId);
      const toIdx = ids.indexOf(toId);
      if (fromIdx < 0 || toIdx < 0) return;
      const next = [...ids];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, fromId);
      try {
        await reorderColumns({
          variables: {
            input: { projectId, orderedColumnIds: next },
          },
          refetchQueries: [
            { query: GET_PROJECT_COLUMNS, variables: { projectId } },
          ],
          awaitRefetchQueries: false,
        });
      } catch (err: any) {
        toast.error(err?.message || 'Nie udało się przenieść kolumny');
        refetchColumns().catch(() => {});
      }
      return;
    }

    // Task move
    if (activeData?.type !== 'task') return;
    const draggedTask = activeData.task as TaskCardData;

    let toColumnId: string | null = null;
    let toIndex = 0;

    if (overData?.type === 'task') {
      const overTask = overData.task as TaskCardData;
      toColumnId = overTask.columnId;
      const colTasks = tasksByColumn[toColumnId] || [];
      const overIndex = colTasks.findIndex((t) => t.id === overTask.id);
      // If moving within same column and dragging downward, dnd-kit places "over" before — adjust index by 1 if needed.
      if (overIndex >= 0) {
        toIndex = overIndex;
      } else {
        toIndex = colTasks.length;
      }
    } else if (overData?.type === 'column') {
      toColumnId = overData.columnId as string;
      toIndex = (tasksByColumn[toColumnId] || []).length;
    } else {
      return;
    }

    if (!toColumnId) return;
    if (
      toColumnId === draggedTask.columnId &&
      toIndex === draggedTask.orderInColumn
    ) {
      return;
    }

    try {
      await moveTask({
        variables: {
          input: {
            taskId: draggedTask.id,
            toColumnId,
            toIndex,
          },
        },
        refetchQueries: [
          { query: GET_PROJECT_TASKS, variables: { projectId } },
          { query: GET_PROJECT, variables: { id: projectId } },
        ],
        awaitRefetchQueries: false,
      });
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się przenieść zadania');
      refetchTasks().catch(() => {});
    }
  };

  const handleAddTask = (columnId: string) => {
    setEditingTask(null);
    setCreateColumnId(columnId);
    setTaskDialogOpen(true);
  };

  const handleTaskClick = (task: TaskCardData) => {
    setEditingTask({
      id: task.id,
      name: task.name,
      description: task.description,
      priority: task.priority,
      startDate: task.startDate,
      plannedEndDate: task.plannedEndDate,
      columnId: task.columnId,
      assignees: task.assignees || [],
    });
    setCreateColumnId(null);
    setTaskDialogOpen(true);
  };

  const togglePriorityFilter = (p: TaskPriority) => {
    setFilterPriorities((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p],
    );
  };

  const isLoading = columnsLoading || tasksLoading;

  return (
    <div className="space-y-3">
      {/* Filters bar */}
      <div className="flex flex-wrap items-center gap-2 bg-muted/30 rounded-md border border-border p-2">
        <FilterIcon className="w-4 h-4 text-muted-foreground ml-1" />
        <button
          type="button"
          onClick={() => setFilterMine((v) => !v)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            filterMine
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-foreground border-input hover:bg-accent/30'
          }`}
        >
          Moje
        </button>
        <button
          type="button"
          onClick={() => setFilterUnassigned((v) => !v)}
          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
            filterUnassigned
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-foreground border-input hover:bg-accent/30'
          }`}
        >
          Nieprzypisane
        </button>
        <span className="text-xs text-muted-foreground mx-1">|</span>
        {TASK_PRIORITY_OPTIONS.map((p) => {
          const active = filterPriorities.includes(p);
          return (
            <button
              key={p}
              type="button"
              onClick={() => togglePriorityFilter(p)}
              className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-foreground border-input hover:bg-accent/30'
              }`}
            >
              {TASK_PRIORITY_LABELS[p]}
            </button>
          );
        })}
        <div className="flex items-center gap-2 ml-auto">
          <Search className="w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Szukaj zadań..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-56 h-8"
          />
        </div>
      </div>

      {isLoading && columns.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : columns.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground border border-dashed border-border rounded-md">
          Ten projekt nie ma jeszcze żadnych kolumn. Przejdź do zakładki
          „Ustawienia”, aby dodać kolumny tablicy.
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={columns.map((c) => `col-sort-${c.id}`)}
            strategy={horizontalListSortingStrategy}
          >
            <div className="flex gap-3 overflow-x-auto pb-3 min-h-[60vh]">
              {columns.map((c) => (
                <KanbanColumn
                  key={c.id}
                  column={c}
                  tasks={tasksByColumn[c.id] || []}
                  onAddTask={handleAddTask}
                  onTaskClick={handleTaskClick}
                  canConfigure={canConfigure}
                  canCreateTasks={canCreateTasks}
                  isColumnDraggable={canConfigure}
                />
              ))}
            </div>
          </SortableContext>

          <DragOverlay>
            {activeDragTask ? (
              <div className="opacity-90 rotate-2">
                <TaskCard task={activeDragTask} onClick={() => {}} isDragging />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <TaskDialog
        open={taskDialogOpen}
        onClose={() => {
          setTaskDialogOpen(false);
          setEditingTask(null);
          setCreateColumnId(null);
        }}
        projectId={projectId}
        task={editingTask}
        defaultColumnId={createColumnId}
        columns={columns.map((c) => ({ id: c.id, name: c.name }))}
        currentUserId={currentUserId}
        canManageProject={canManageProject}
      />
    </div>
  );
};
